// AudioManager.js
// Example usage:
//
// import { audio } from './AudioManager';
//
// BASIC USAGE:
// const s = audio.playSfx(SFX.CLICK, { rate: 1.2 });
// const v = audio.playVo(VO.INTRO, { timeoutMs: 6000 });
//
// FADE IN/OUT:
// const m = audio.playMusic(MUSIC.LOBBY, { fadeInMs: 1000, fadeOutMs: 500 });
// m.handle.stop();  // uses fadeOutMs from options (500ms) or 0 if not specified
// m.handle.stop(200);  // override with custom fade (200ms)
//
// ARRAY USAGE (for VO/SFX with multiple variations):
// const v = audio.playVo(VO.POSITIVE_DROP);                    // sequential through shuffled array
// const s = audio.playSfx(SFX.IMPACTS, { pureRandom: true }); // purely random every time
//
// HANDLERS:
// const p = audio.playMusic(MUSIC.LOBBY, { fadeInMs: 400 });
// p.handle.stop();             // stop with fadeOutMs from options or 0
// await p;                     // resolves after playback ends/stops (or timeout)
//
//
// PREFERRED - ABORTCONTROLLER:
// const ac = new AbortController();
// const m = audio.playMusic(MUSIC.ROUND, { signal: ac.signal });
// ac.abort();                  // cancels all tracks with the signal
// await m;                     // resolves after stop
//
//
// TIMED CUES (fire something at an offset INSIDE a clip):
// Play a long opening sting; 16s in, fade up the looping bed underneath it.
// audio.playMusic(MUSIC.OPENING_STING, {
//     loop: false,
//     fadeOutMs: 1500,
//     signal: ac.signal,
//     cues: [
//         { at: 16000, run: () => audio.playMusic(MUSIC.INTRO, { loop: true, fadeInMs: 4000, signal: ac.signal }) },
//         // { at: { fromEnd: 2000 }, run: () => ... }  // anchor to the clip's tail instead
//     ],
// });
// Cues are cleared automatically when the clip stops / ends / aborts. You can
// also add one live: const h = audio.playMusic(...).handle; h.addCue(16000, () => {...});
//
//
// FOR SEQUENCED AUDIO:
// import { audio, music, sfx, vo, wait } from './AudioManager';
//
// await audio.sequence([
//    music(MUSIC.MENU, { fadeInMs: 1000, volume: 0.6 }),
//    wait(500),
//    vo(VO.WELCOME),
//    wait(1000),
//    sfx(SFX.WHOOSH),
//    wait(200),
//    sfx(SFX.DING, { volume: 0.8 }),
// ]);

import { Howl, Howler } from "howler";
import { audioDiag, LEVEL, clipName } from "services/audioDiagnostics";

const DEFAULT_AUDIO_FORMATS = ["mp3", "ogg"];

// Concurrency caps (oldest-drop when exceeded)
const MAX_CONCURRENT_MUSIC = 2;
const MAX_CONCURRENT_SFX = 5;
const MAX_CONCURRENT_VO = 1;

/**
 * Slack on top of a clip's length before an awaited play gives up on its `end`.
 *
 * A browser that blocks sound until the page is clicked (Firefox does by
 * default) never starts the clip: Howler holds the play until audio unlocks,
 * so `end` never comes and anything awaiting it hangs. A host page nobody has
 * clicked then waited out every VO safety timeout, about two minutes a
 * question. Waiting the clip's own length instead keeps a silent page at the
 * same pace as one with sound. This only needs to cover a context that is slow
 * to resume, so keep it small: it's added to every clip on a silent page.
 */
const PLAY_END_GRACE_MS = 500;

/**
 * Bytes of decoded PCM a clip of `seconds` occupies once Web Audio has it.
 *
 * This is the number that decides whether a low-end TV can hold the clip at
 * all. Web Audio does not keep the MP3 — it expands it to float32 samples per
 * channel at the AudioContext's rate, so a 640KB / 32s mono bed becomes ~6MB
 * resident, and the same file in stereo ~12MB. Compression ratio is irrelevant;
 * only DURATION and CHANNELS matter. An `html5: true` clip streams and is not
 * counted here, because it never becomes a buffer.
 *
 * Used for the overlay's budget readout, and it is an estimate: we assume
 * stereo, because Howler does not expose the decoded buffer's channel count,
 * so a mono clip reads high by 2x. Upper bound is the useful direction for a
 * memory budget.
 */
function estimateDecodedBytes(seconds) {
    const rate = Howler.ctx?.sampleRate || 44100;
    return Math.round((seconds || 0) * rate * 4 * 2);
}

/**
 * Once this device has shown it can't decode, clips at least this long are
 * streamed instead. Short clips keep decoding either way: they're cheap to
 * hold, and Web Audio gives them the sample-accurate start that stings need.
 */
const STREAM_MIN_SECONDS = 8;

/**
 * Ceiling on decoded audio held in the cache, in bytes.
 *
 * Without one the cache only ever loses what a caller explicitly evicts, so
 * anything nobody thought to release — every VO line a game plays, say — stays
 * for the session. A Tizen set reached 114MB by question four that way, with a
 * decode failing and a music bed cutting out mid-loop.
 *
 * 64MB leaves room for what a question actually needs at once (bed, stings,
 * applause, the current read-outs, the end-game set) with headroom, while
 * being far enough below where that device got into trouble. Lower it if
 * playback still glitches; raise it if the overlay shows clips being evicted
 * and immediately reloaded.
 */
const CACHE_BUDGET_BYTES = 64 * 1024 * 1024;

/* ---------------- Utils ---------------- */

function normalizeDefinition(definitionOrUrl, defaults = {}) {
    if (typeof definitionOrUrl === "string") {
        return { src: [definitionOrUrl], ...defaults };
    }
    if (definitionOrUrl && typeof definitionOrUrl === "object") {
        const url = definitionOrUrl.url ?? definitionOrUrl.src;
        if (typeof url === "string" && url) return { ...defaults, ...definitionOrUrl, src: [url] };
        if (Array.isArray(url) && url.length) return { ...defaults, ...definitionOrUrl, src: url.slice() };
    }
    return null;
}

function clamp01(value) { return Math.min(1, Math.max(0, value)); }
function delay(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }
function cacheKey(def) {
    const s = def?.src;
    return !s ? "" : Array.isArray(s) ? s.join("|") : String(s);
}
function waitForHowlLoad(howl, timeoutMs = 8000) {
    return new Promise((resolve, reject) => {
        let done = false;
        const cleanup = () => {
            try { howl.off("load", onLoad); } catch { }
            try { howl.off("loaderror", onErr); } catch { }
        };
        const onLoad = () => { if (!done) { done = true; cleanup(); resolve(howl); } };
        const onErr = (_id, msg) => { if (!done) { done = true; cleanup(); reject(new Error(msg || "loaderror")); } };
        howl.once("load", onLoad);
        howl.once("loaderror", onErr);
        if (timeoutMs > 0) {
            setTimeout(() => {
                if (!done) { done = true; cleanup(); reject(new Error("load timeout")); }
            }, timeoutMs);
        }
    });
}

/**
 * Handle object returned on any playX call (music/sfx/vo).
 * @typedef {Object} PlayHandle
 * @property {number|null} id - Howler id, available after start.
 * @property {(fadeMs?:number)=>void} stop - Stop this audio now (uses fadeOutMs from options if fadeMs not provided, or 0).
 * @property {(volume:number, fadeMs?:number)=>void} fadeTo - Fade to a volume.
 * @property {(at:number|{fromEnd:number}, run:(handle:PlayHandle)=>void)=>void} [addCue] - Schedule a one-shot cue at an offset inside the clip (ms-from-start or { fromEnd }). No-op before start.
 * @property {()=>void} [clearCues] - Cancel any pending timed cues on this clip.
 * @property {()=>boolean} playing - Whether this audio is currently playing.
 * @property {Promise<{howl:Howl, id:number, startedAt:number}|null>} whenStarted - Resolves when playback begins.
 * @property {Promise<void>} whenEnded - Resolves when playback ends/stops.
 */

/**
 * Create a resolved Promise with a no-op handle (for invalid inputs / failures).
 * @returns {Promise<void> & { handle: PlayHandle }}
 */
function createNoopPlayPromise() {
    const p = Promise.resolve();
    Object.defineProperty(p, "handle", {
        value: {
            get id() { return null; },
            stop() { },
            fadeTo() { },
            playing() { return false; },
            whenStarted: Promise.resolve(null),
            whenEnded: Promise.resolve(),
        },
        enumerable: false,
        configurable: false,
        writable: false,
    });
    return /** @type {any} */ (p);
}

/* =======================================================
 *                    AudioManager
 * ======================================================= */
export class AudioManager {
    constructor() {
        this.isReady = false;

        this.musicDefaultVolume = 0.75;
        this.musicMuted = false;

        this.sfxDefaultVolume = 1.0;
        this.sfxMuted = false;

        this.voDefaultVolume = 0.95;
        this.voMuted = false;

        // Ducking — music (and any registered listeners, e.g. video elements)
        // drop to duckLevel while VO is playing. The fade is applied with
        // duckFadeMs on both directions.
        this.duckLevel = 1; // Pointless: old manager did not duck music (template 0.25)
        this.duckFadeMs = 300;
        this._musicUnduckedVolumes = new Map();
        // SFX (stings, applause) duck under VO too, but less than music so the
        // stings keep some punch under the host.
        this.sfxDuckLevel = 1; // Pointless: old manager did not duck SFX (template 0.4)
        this._sfxUnduckedVolumes = new Map();
        this._isDucked = false;
        /**
         * Named holds that keep music ducked across a multi-VO beat (e.g. the host
         * reading a question and its four answers), so it doesn't bounce back up in
         * the gaps between clips. Music unducks only when no VO is playing AND no
         * hold is open. @type {Set<string>}
         */
        this._duckHolds = new Set();
        /** @type {Set<(ducked: boolean) => void>} */
        this._duckListeners = new Set();

        /** @type {Map<string, Howl>} */
        this.cache = new Map();

        /** @type {{howl: Howl, id: number, startedAt: number, targetVolume: number}[]} */
        this.activeMusic = [];
        /** @type {{howl: Howl, id: number, startedAt: number, targetVolume: number}[]} */
        this.activeSfx = [];
        /** @type {{howl: Howl, id: number, startedAt: number, targetVolume: number}[]} */
        this.activeVo = [];

        /** @type {Map<any, {shuffled: any[], index: number}>} */
        this.arrayTracking = new Map();

        /** Active crossfade-loop controllers, so master stops can cancel them. @type {Set<{cancel: () => void}>} */
        this._musicLoops = new Set();

        /** @type {import('utils/seededRandom').SeededRandom|null} */
        this._seededRng = null; // Pointless: seeded RNG for mirror-synced VO picks

        this.hasUnlocked = false;
        this.onVisibilityChangeBound = this.onVisibilityChange.bind(this);

        /**
         * Stream long clips instead of decoding them to PCM.
         *
         * OFF by default, on evidence from a Tizen set: with five clips
         * streaming at boot, exactly ONE reached `readyState 4` and the other
         * four went `loadstart → suspend → abort` and stopped at `readyState 0`
         * / `networkState IDLE`. No error, no missing source — the TV started
         * fetching and gave up. It will service about one media element at a
         * time, so streaming everything long is worse than not streaming.
         *
         * Decoding, meanwhile, was never given a fair run: the preload timeout
         * used to be 8s, which cut a 24s clip off mid-decode and reported it as
         * a failure. With the boot set down to seven clips (~25MB, not ~190MB)
         * and a 15s timeout, decode is the better bet on this hardware.
         *
         * A genuine decode failure still flips this on for the rest of the
         * session, so a device that really can't decode adapts on its own. See
         * `_fallbackToStreaming`.
         */
        this._streamingMode = false;

        /**
         * The one background prime queue, shared by every caller so they can't
         * compete for the device's single media pipeline. `_primeNow` is for
         * audio about to be played and jumps ahead of `_primeIdle`.
         * @type {{clip: any, resolve: (ok: boolean) => void, timeoutMs?: number}[]}
         */
        this._primeNow = [];
        this._primeIdle = [];
        this._priming = false;
    }

    /** Whether music/video are currently ducked because a VO is playing. */
    get isDucked() { return this._isDucked; }

    /**
     * Subscribe to duck state changes. The callback is invoked with `true` when
     * ducking begins and `false` when it ends. Used by VideoPlayer to mirror
     * the music duck against the inline video element.
     * @param {(ducked: boolean) => void} cb
     * @returns {() => void} Unsubscribe function
     */
    addDuckListener(cb) {
        this._duckListeners.add(cb);
        return () => { this._duckListeners.delete(cb); };
    }

    _setDucked(ducked) {
        if (this._isDucked === ducked) return;
        this._isDucked = ducked;
        this._duckListeners.forEach(cb => {
            try { cb(ducked); } catch { }
        });
    }

    _duckMusic() {
        this.activeMusic.forEach(e => {
            if (!this._musicUnduckedVolumes.has(e.id)) {
                this._musicUnduckedVolumes.set(e.id, e.targetVolume);
            }
            const duckedVol = this.musicMuted ? 0 : this._musicUnduckedVolumes.get(e.id) * this.duckLevel;
            this._fadeEntry(e, duckedVol, this.duckFadeMs);
        });
    }

    _unduckMusic() {
        this.activeMusic.forEach(e => {
            const originalVol = this._musicUnduckedVolumes.get(e.id) ?? e.targetVolume;
            const restoreVol = this.musicMuted ? 0 : originalVol;
            this._fadeEntry(e, restoreVol, this.duckFadeMs);
        });
        this._musicUnduckedVolumes.clear();
    }

    // SFX duck like music, to sfxDuckLevel. An entry already fading out (its timed
    // fadeOutMs tail, or a stop) is left alone, or an unduck would lift it back up.
    _duckSfx() {
        this.activeSfx.forEach(e => {
            if (e.fadingOut) return;
            if (!this._sfxUnduckedVolumes.has(e.id)) {
                this._sfxUnduckedVolumes.set(e.id, e.targetVolume);
            }
            const duckedVol = this.sfxMuted ? 0 : this._sfxUnduckedVolumes.get(e.id) * this.sfxDuckLevel;
            this._fadeEntry(e, duckedVol, this.duckFadeMs);
        });
    }

    _unduckSfx() {
        this.activeSfx.forEach(e => {
            if (e.fadingOut) return;
            const originalVol = this._sfxUnduckedVolumes.get(e.id) ?? e.targetVolume;
            const restoreVol = this.sfxMuted ? 0 : originalVol;
            this._fadeEntry(e, restoreVol, this.duckFadeMs);
        });
        this._sfxUnduckedVolumes.clear();
    }

    /** Duck music + SFX under VO and notify listeners (e.g. VideoPlayer). */
    _duck() {
        this._duckMusic();
        this._duckSfx();
        this._setDucked(true);
    }

    /** Bring music + SFX back up and notify listeners. */
    _unduck() {
        this._unduckMusic();
        this._unduckSfx();
        this._setDucked(false);
    }

    /**
     * Keep music + SFX ducked until `releaseDuck(key)`, even between VO clips.
     * Ducks now if it isn't already. Idempotent per key.
     * @param {string} key
     */
    holdDuck(key) {
        this._duckHolds.add(key);
        this._duck();
    }

    /**
     * Release a hold from holdDuck. Music unducks once no VO is playing and no
     * other hold is open. Releasing an unknown key is a no-op, so cleanup paths
     * can call it unconditionally.
     * @param {string} key
     */
    releaseDuck(key) {
        if (!this._duckHolds.delete(key)) return;
        this._maybeUnduck();
    }

    /** Unduck if nothing still needs the duck (no active VO, no open hold). */
    _maybeUnduck() {
        if (this.activeVo.length > 0 || this._duckHolds.size > 0) return;
        this._unduck();
    }

    /** Initialize Howler and unlock listeners (idempotent). */
    init() {
        if (this.isReady) return;

        // Treat a streamed clip as ready at `canplay`, not `canplaythrough`.
        //
        // Howler marks an HTML5 clip loaded only on `canplaythrough`, and its
        // play() queues until that fires. A TV buffers lazily and often never
        // gets there: the clip sits in `loading` for ever, nothing plays, and
        // there is no error because nothing failed. That is exactly what the
        // Tizen set did — five streamed clips stuck loading, zero errors.
        //
        // `canplay` is readyState 3 (HAVE_FUTURE_DATA), which is the same bar
        // Howler's own html5 play path checks before starting (`readyState >=
        // 3`), so this makes the ready signal agree with the play condition
        // instead of sitting a whole readyState above it. Must be set before
        // any Howl is built, which is why it's here.
        Howler._canPlayEvent = "canplay";

        Howler.autoUnlock = true;
        document.addEventListener("visibilitychange", this.onVisibilityChangeBound, false);
        this.wireUnlockListeners();
        this.isReady = true;
    }

    /** Cleanup and unload all audio. */
    destroy() {
        document.removeEventListener("visibilitychange", this.onVisibilityChangeBound);
        this._musicLoops.forEach(c => { try { c.cancel(); } catch { } });
        this._musicLoops.clear();
        this.stopAllMusic();
        this.stopAllSfx();
        this.stopAllVo();
        this.cache.forEach(h => { try { h.unload(); } catch { } });
        this.cache.clear();
        this.activeMusic.length = 0;
        this.activeSfx.length = 0;
        this.activeVo.length = 0;
        this.arrayTracking.clear();
        // No stale duck survives into the next mount.
        this._duckHolds.clear();
        this._musicUnduckedVolumes.clear();
        this._sfxUnduckedVolumes.clear();
        this._setDucked(false);
        // The instance is a module singleton that outlives its components, so let
        // the next mount's init() re-arm the listeners removed above.
        this.isReady = false;
    }

    wireUnlockListeners() {
        const tryUnlock = () => {
            if (!this.hasUnlocked) {
                try { Howler.mute(false); } catch { }
                this.hasUnlocked = true;
            }
            ["pointerdown", "keydown", "click", "touchstart"].forEach(n => window.removeEventListener(n, tryUnlock));
        };
        ["pointerdown", "keydown", "click", "touchstart"].forEach(n => window.addEventListener(n, tryUnlock, { passive: true }));
    }

    onVisibilityChange() {
        if (document.hidden) {
            this.setMasterMute(true);
        } else {
            this.setMasterMute(false);
        }
    }

    /* ---------------- Seeded Random Support ---------------- */

    /**
     * Pointless: set a seeded RNG for deterministic random audio selection (mirror sync).
     * Hand-ported from the old audioManager.js.
     * @param {import('utils/seededRandom').SeededRandom} rng
     */
    setSeededRandom(rng) {
        this._seededRng = rng || null;
    }

    /* ---------------- Array Tracking Utils ---------------- */

    /**
     * Fisher-Yates shuffle algorithm
     * @param {Array} array
     * @returns {Array}
     */
    _shuffleArray(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = this._seededRng
                ? this._seededRng.nextInt(i + 1)
                : Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    /**
     * Get the next item from an array, handling sequential or random playback
     * @param {any} arrayRef - The original array reference
     * @param {boolean} pureRandom - If true, purely random. If false, sequential through shuffled array.
     * @returns {any}
     */
    _getFromArray(arrayRef, pureRandom = false) {
        if (!Array.isArray(arrayRef) || arrayRef.length === 0) {
            return null;
        }

        // Purely random - no tracking needed
        if (pureRandom) {
            return arrayRef[this._seededRng
                ? this._seededRng.nextInt(arrayRef.length)
                : Math.floor(Math.random() * arrayRef.length)];
        }

        // Sequential through shuffled array
        let tracking = this.arrayTracking.get(arrayRef);

        if (!tracking) {
            // First time seeing this array - shuffle and initialize
            tracking = {
                shuffled: this._shuffleArray(arrayRef),
                index: 0
            };
            this.arrayTracking.set(arrayRef, tracking);
        }

        const item = tracking.shuffled[tracking.index];
        tracking.index++;

        // Loop back to start
        if (tracking.index >= tracking.shuffled.length) {
            tracking.index = 0;
            //tracking.shuffled = this._shuffleArray(arrayRef); //could add a flag to enable this
        }

        return item;
    }


    /**
     * Resolve an audio reference that might be an array
     * @param {any} ref - Could be a single audio definition or an array of them
     * @param {boolean} pureRandom - If true and ref is array, pick randomly. If false, sequential.
     * @returns {any}
     */
    _resolveArrayReference(ref, pureRandom = false) {
        if (Array.isArray(ref)) {
            return this._getFromArray(ref, pureRandom);
        }
        return ref;
    }

    /* ---------------- internals ---------------- */

    /**
     * Silence a play Howler is still holding for an audio unlock, before we
     * stop it. The stop is queued behind the held play, so on the first click
     * the clip would start and only stop a moment later: an audible blip. Set
     * on the sound directly, as mute() waits in that same queue. Howler clears
     * it when the sound is reused. No-op for a clip that's actually playing.
     */
    _silenceIfHeld(howl, id) {
        if (howl.playing(id)) return false;
        const sound = howl._soundById?.(id);
        if (sound) sound._muted = true;
        return true;
    }

    /**
     * Fade a tracked entry (duck, unduck, fadeTo). If Howler is still holding
     * its play for an audio unlock, the fade would be queued, and Howler
     * replays that queue one fade after another once the clip starts: on the
     * first click the bed swelled in and out through every duck since. So
     * keep only the latest level and apply it when the clip really starts.
     */
    _fadeEntry(e, to, ms) {
        if (!e.howl.playing(e.id)) {
            if (e.pendingVolume === undefined) {
                e.howl.once("play", () => {
                    const v = e.pendingVolume;
                    e.pendingVolume = undefined;
                    try { e.howl.fade(e.howl.volume(e.id), v, ms, e.id); } catch { }
                }, e.id);
            }
            e.pendingVolume = to;
            return;
        }
        try { e.howl.fade(e.howl.volume(e.id), to, ms, e.id); } catch { }
    }

    _stopEntry(entry, fadeMs = 80) {
        if (!entry?.howl || entry.id == null) return;
        this._silenceIfHeld(entry.howl, entry.id);
        entry.fadingOut = true; // duck/unduck must not fight this fade
        try {
            const v = entry.howl.volume(entry.id);
            try { entry.howl.fade(v, 0, fadeMs, entry.id); } catch { }
            setTimeout(() => { try { entry.howl.stop(entry.id); } catch { } }, fadeMs + 10);
        } catch { }
    }

    _removeById(list, id) {
        const i = list.findIndex(e => e.id === id);
        if (i >= 0) list.splice(i, 1);
    }

    _enforceLimit(list, max, fadeMs = 80) {
        while (list.length >= max) {
            const oldest = list.shift();
            this._stopEntry(oldest, fadeMs);
        }
    }

    /**
     * Should this clip stream rather than decode?
     *
     * Only once the device has already failed a decode, and only for clips long
     * enough to be worth it. `def.html5` forces it for a single clip, which is
     * what `_fallbackToStreaming` uses to rebuild the one that just failed.
     */
    _shouldStream(def) {
        if (def?.html5 === true) return true;
        return this._streamingMode && (def?.seconds ?? 0) >= STREAM_MIN_SECONDS;
    }

    /**
     * Get the cached Howl for a definition, creating it on first use.
     *
     * A streaming Howl plays from an `<audio>` element rather than decoding the
     * whole file to PCM up front: a little more start latency and less mixing
     * precision, in exchange for near-zero resident memory. Which path a clip
     * takes is decided by `_shouldStream`, from what this device has already
     * proved it can do — see `_fallbackToStreaming`.
     *
     * Every Howl gets permanent `load` / `loaderror` listeners here, once, at
     * creation. They are for diagnostics only and are deliberately NOT the
     * per-play listeners — a decode failure has to be recorded even when
     * nothing happens to be waiting on that clip at the time.
     */
    _getOrCreateHowl(def, cfg = {}) {
        const key = cacheKey(def);
        if (!key) return null;
        let howl = this.cache.get(key);
        // Every play and every prime comes through here, so this is the one
        // place that reliably means "this clip was wanted". That stamp is what
        // the budget evictor sorts on.
        if (howl) {
            howl._gcUsedAt = Date.now();
            return howl;
        }
        {
            const streaming = this._shouldStream(def);
            howl = new Howl({
                src: def.src,
                preload: true,
                html5: streaming,
                volume: clamp01(cfg.volume ?? 0), // start silent; we'll fade/set volume after play
                format: DEFAULT_AUDIO_FORMATS,
            });
            howl._gcStreaming = streaming;
            howl._gcSrc = def.src;
            if (streaming) this._instrumentStream(howl);
            howl.on("load", () => {
                howl._gcBytes = streaming ? 0 : estimateDecodedBytes(howl.duration() || 0);
                audioDiag.count("loaded");
                // The only moment the cache can grow, and it covers loads from
                // playback as well as from priming — so the budget can't be
                // sidestepped by any path that skips primeClip.
                this._evictToBudget();
            });
            howl.on("loaderror", (_id, msg) => {
                // A Howl we deliberately threw away on the way to a streaming
                // retry. Howler's decode callback checks `_sounds.length` and
                // unload() empties it, so a decode that lands after we've
                // discarded the Howl reports itself as 'Decoding audio data
                // failed.' That error is ours, not the device's, and counting
                // it made a working stream retry look like a hard failure.
                if (howl._gcDiscarded) return;

                // The failure that kills music on a low-end TV. Howler's own
                // message for it is 'Decoding audio data failed.' — out of room
                // for the decoded buffer, not a missing or corrupt file.
                audioDiag.count("loadError");
                audioDiag.record(
                    LEVEL.ERROR, "load-failed", def.src,
                    `${msg || "loaderror"}${streaming ? " (streaming)" : " (decode)"}`
                );
                // One failed decode is the device telling us what it can do.
                // Take it at its word for every long clip from here on, so the
                // rest of the game doesn't repeat the same wasted fetch+decode.
                if (!streaming && !this._streamingMode) {
                    this._streamingMode = true;
                    audioDiag.record(
                        LEVEL.WARN, "streaming-mode", def.src,
                        `decode failed — long clips (>${STREAM_MIN_SECONDS}s) will stream from now on`
                    );
                }
            });
            howl._gcUsedAt = Date.now();
            this.cache.set(key, howl);
        }
        return howl;
    }

    /**
     * Drop the least recently used clips until the cache is inside its budget.
     *
     * A backstop, not a replacement for callers releasing what they know they
     * are done with (a tier the ladder has passed, a question's read-outs).
     * Those are precise and free; this catches everything nobody thought to
     * release, which on a long game is most of it.
     *
     * Never touches a clip that is currently playing, and never touches a
     * stream (it holds no buffer, so evicting one frees nothing and only costs
     * a re-fetch). Runs after each load, which is the only time the total can
     * grow.
     */
    _evictToBudget() {
        let total = this.cacheStats().decodedBytes;
        if (total <= CACHE_BUDGET_BYTES) return;

        const candidates = [...this.cache.entries()]
            .filter(([, h]) => (h._gcBytes || 0) > 0 && !h.playing())
            .sort((a, b) => (a[1]._gcUsedAt || 0) - (b[1]._gcUsedAt || 0));

        let freed = 0, dropped = 0;
        for (const [key, howl] of candidates) {
            if (total <= CACHE_BUDGET_BYTES) break;
            const bytes = howl._gcBytes || 0;
            try { howl.unload(); } catch { }
            this.cache.delete(key);
            total -= bytes;
            freed += bytes;
            dropped++;
            audioDiag.count("evicted");
        }

        if (dropped) {
            // One line per pass, not per clip, so a big sweep can't flood the
            // event log and push the errors off the end of it.
            audioDiag.record(
                LEVEL.INFO, "budget-evict", "",
                `${dropped} clips, ${(freed / 1048576).toFixed(1)}MB freed, now ${(total / 1048576).toFixed(1)}MB`
            );
        }
    }

    /**
     * Watch the real `<audio>` element behind a streamed clip.
     *
     * A stream that never becomes playable raises no error — it just sits
     * there, and every layer above it reports "fine". The only place the truth
     * exists is the media element itself, so this records which media events it
     * actually fired. Read alongside `readyState` / `networkState` (see
     * `streamDiagnostics`) it separates the cases that otherwise look alike:
     * a clip the device won't buffer at all, one that buffers but never reaches
     * Howler's ready bar, and one whose source is broken.
     *
     * Listeners only add to a Set, and the element is one Howler already owns,
     * so this costs nothing and changes no behaviour.
     */
    _instrumentStream(howl) {
        const node = howl?._sounds?.[0]?._node;
        if (!node || typeof node.addEventListener !== "function") return;
        howl._gcEvents = new Set();
        [
            "loadstart", "loadedmetadata", "loadeddata", "canplay", "canplaythrough",
            "playing", "waiting", "stalled", "suspend", "abort", "emptied", "error",
        ].forEach(ev => {
            node.addEventListener(ev, () => howl._gcEvents.add(ev), false);
        });
    }

    /**
     * Per-stream state, for the debug overlay. One row per streamed clip.
     * @returns {{name:string, readyState:number, networkState:number, state:string, events:string}[]}
     */
    streamDiagnostics() {
        const rows = [];
        this.cache.forEach(howl => {
            if (!howl._gcStreaming) return;
            const node = howl?._sounds?.[0]?._node;
            rows.push({
                name: clipName(howl._gcSrc),
                state: howl.state(),
                readyState: node?.readyState ?? -1,
                networkState: node?.networkState ?? -1,
                events: [...(howl._gcEvents ?? [])].join(","),
            });
        });
        return rows;
    }

    /**
     * Rebuild a clip that failed to decode as a streaming clip, and return it.
     *
     * The recovery for the clip that actually tripped the switch. `loaderror`
     * has already flipped `_streamingMode`, so everything created AFTER this
     * point streams on its own — but the clip in hand is still the broken
     * decoded one, cached and useless. This throws it away and puts a streaming
     * Howl at the same cache key, so the caller can carry straight on with it.
     *
     * Returns null when there's nothing to do (already streaming, or no such
     * clip), which is also what stops this from retrying forever.
     *
     * @param {object} def - normalized definition
     * @returns {Howl|null}
     */
    _fallbackToStreaming(def) {
        const key = cacheKey(def);
        if (!key) return null;
        const failed = this.cache.get(key);
        if (!failed || failed._gcStreaming) return null;

        // Mark BEFORE unloading: unload() makes any in-flight decode report a
        // failure, and that report is an artefact of this retry, not a fault.
        failed._gcDiscarded = true;
        try { failed.unload(); } catch { }
        this.cache.delete(key);

        audioDiag.record(LEVEL.WARN, "stream-retry", def.src, "retrying as a stream");
        return this._getOrCreateHowl({ ...def, html5: true }, { volume: 0 });
    }

    /**
     * What the audio cache currently holds. Feeds the debug overlay's budget
     * line — the single number that says whether this device is near its limit.
     * @returns {{clips:number, loaded:number, failed:number, streaming:number, decodedBytes:number}}
     */
    cacheStats() {
        let loaded = 0, failed = 0, loading = 0, streaming = 0, decodedBytes = 0;
        this.cache.forEach(howl => {
            const state = howl.state();
            if (state === "loaded") loaded++;
            else if (state === "unloaded") failed++;   // created but holding nothing
            // Stuck here is its own failure mode, and a silent one: no error is
            // ever raised, the clip just never becomes playable. Counted
            // separately so it can't hide inside a "4/8 loaded" again.
            else loading++;
            if (howl._gcStreaming) streaming++;
            decodedBytes += howl._gcBytes || 0;
        });
        return {
            clips: this.cache.size,
            loaded, failed, loading, streaming, decodedBytes,
            budgetBytes: CACHE_BUDGET_BYTES,
            streamingMode: this._streamingMode,
        };
    }

    /**
     * The Web Audio context's state — "running", "suspended", or "n/a" if the
     * device gave us no context at all and Howler fell back to HTML5 Audio.
     * A stuck "suspended" is the OTHER way audio goes silent (no unlock
     * gesture), and it looks identical from the outside, so the overlay shows
     * both this and `hasUnlocked` side by side.
     */
    contextState() {
        try { return Howler.ctx?.state ?? "n/a"; } catch { return "n/a"; }
    }

    /**
     * The real unlock picture, which is two separate things, not one.
     *
     * `this.hasUnlocked` only means "an input event reached us" — it says
     * nothing about whether audio can actually play, and reporting it as
     * "unlocked yes" was misleading while HTML5 audio was in fact locked.
     *
     * Web Audio and HTML5 Audio unlock independently. Howler builds its pool of
     * usable `<audio>` elements inside the gesture handler, so before a gesture
     * `_obtainHtml5Audio()` hands back a raw, locked element (howler.core.js,
     * `_obtainHtml5Audio`). A locked element told to load goes
     * `loadstart → suspend → abort` and never gets any data, with no error.
     * `html5Pool` of 0 alongside streams stuck at readyState 0 is that exact
     * situation.
     *
     * @returns {{sawInput:boolean, webAudio:boolean, html5Pool:number}}
     */
    unlockState() {
        let webAudio = false, html5Pool = 0;
        try { webAudio = !!Howler._audioUnlocked; } catch { }
        try { html5Pool = Howler._html5AudioPool?.length ?? 0; } catch { }
        return { sawInput: this.hasUnlocked, webAudio, html5Pool };
    }

    /**
     * Clips with more than one copy actually sounding, straight from Howler,
     * e.g. `[{ name: "bed_block1", count: 2 }]`.
     *
     * The tracked lists (activeMusic etc.) only hold what we think is playing;
     * a copy that slipped out of them, like a held play started by an unlock,
     * shows up here and nowhere else. A bed stacked on itself sounds louder and
     * crushed. The 80ms crossfade overlap is the one legitimate count of 2.
     *
     * @returns {{name: string, count: number}[]}
     */
    stackedClips() {
        const out = [];
        for (const howl of this.cache.values()) {
            let count = 0;
            try { count = howl._sounds.filter(s => !s._paused).length; } catch { }
            if (count > 1) out.push({ name: clipName(howl._src), count });
        }
        return out;
    }

    /* ---------------- Prime / Preload ----------------
     *
     * ONE CLIP AT A TIME, ALWAYS. Every preload in the app goes through a
     * single queue owned by this class, whatever the caller does. That's the
     * shape of the hardware this has to run on: a low-end TV services roughly
     * one media operation at once, and asked for several it starts them all
     * and finishes few. It isn't something call sites should have to know or
     * remember — they just ask for a clip.
     *
     * Two lanes. `primeClip` is "I want this soon" and jumps ahead;
     * `primeInBackground` is bulk work that waits. That alone fixes the
     * collisions we hit: the end-game set decoding the 32.6s closing titles at
     * the exact moment Q1 wanted its bed, and a question's five read-out lines
     * being fired off together.
     *
     * Playback does NOT queue. `_play` loads directly, because something the
     * game is playing right now must never wait behind a preload.
     */

    /**
     * Preload a single clip, ahead of any bulk work already queued.
     * Resolves when loaded (or rejects on error/timeout).
     * @param {string|{src:string[]}|{src:string}|{url:string}} clip
     * @param {{timeoutMs?: number, volume?: number, priority?: "now"|"idle"}} [opts]
     * @returns {Promise<Howl>}
     */
    primeClip(clip, opts = {}) {
        return new Promise((resolve, reject) => {
            const lane = opts.priority === "idle" ? this._primeIdle : this._primeNow;
            lane.push({ clip, opts, resolve, reject });
            this._drainPrimeQueue();
        });
    }

    /** Run the shared prime queue to empty, one clip at a time. Idempotent. */
    async _drainPrimeQueue() {
        if (this._priming) return;
        this._priming = true;
        try {
            for (;;) {
                // "now" always wins, including work queued while this is running.
                const entry = this._primeNow.shift() ?? this._primeIdle.shift();
                if (!entry) break;
                try { entry.resolve(await this._loadClip(entry.clip, entry.opts)); }
                catch (e) { entry.reject(e); }
            }
        } finally {
            this._priming = false;
        }
    }

    /** The actual load. Never call directly — go through `primeClip`. */
    async _loadClip(clip, opts = {}) {
        this.init();
        const def = normalizeDefinition(clip);
        if (!def) throw new Error("[AudioManager] primeClip: invalid clip");

        // Generous, because the thing being waited on is slow on the devices
        // that matter: a TV decoding a 20s+ clip, or buffering a stream to
        // canplaythrough. The old 8s cut clips off mid-decode and turned a slow
        // load into a "failure" plus a pointless retry.
        const timeoutMs = opts.timeoutMs ?? 15000;

        const howl = this._getOrCreateHowl(def, { volume: opts.volume ?? 0 });
        const st = howl.state();
        if (st === "loaded") return howl;
        if (st === "unloaded") { try { howl.load(); } catch { } }

        // Don't WAIT on a streamed clip. Creating the Howl has already started
        // it buffering, and a stream is by definition something we're happy to
        // start before it's all here — waiting for it is the opposite of the
        // point. It also means a slow TV can't hold the boot screen hostage:
        // the five long clips used to be the whole of the preload wait, and
        // now they cost nothing. If one isn't ready when it's asked for,
        // Howler queues the play and starts it the moment it can.
        if (howl._gcStreaming) {
            audioDiag.count("primed");
            return howl;
        }

        try {
            const loaded = await waitForHowlLoad(howl, timeoutMs);
            audioDiag.count("primed");
            return loaded;
        } catch (e) {
            // A timeout here is its own signal: the clip didn't fail outright,
            // it's still grinding. On a TV that usually means the decode queue
            // is backed up behind clips that are too big to be worth holding.
            const msg = e?.message || String(e);
            if (msg === "load timeout") audioDiag.record(LEVEL.WARN, "prime-timeout", def.src, `>${timeoutMs}ms`);

            // Same recovery as the play path: a decode this device has no room
            // for still streams. Bounded to one attempt by _fallbackToStreaming
            // returning null for a clip that was already streaming.
            const streamed = this._fallbackToStreaming(def);
            if (!streamed) throw e;

            if (streamed.state() === "unloaded") { try { streamed.load(); } catch { } }
            const loaded = await waitForHowlLoad(streamed, timeoutMs);
            audioDiag.count("primed");
            return loaded;
        }
    }

    /**
     * Preload many clips. Accepts an array or object map.
     * @param {Array<string|Object>|Record<string,string|Object>} clips
     * @param {{timeoutMs?: number, volume?: number}} [opts]
     * @returns {Promise<void>}
     */
    async prime(clips, opts = {}) {
        if (!clips) return;
        const arr = Array.isArray(clips) ? clips : Object.values(clips);

        // Flatten any arrays within the clips
        const flatClips = [];
        for (const c of arr) {
            if (Array.isArray(c)) {
                flatClips.push(...c);
            } else {
                flatClips.push(c);
            }
        }

        await Promise.all(flatClips.map(c => this.primeClip(c, opts)));
    }

    /**
     * Prime clips as BULK background work: behind anything urgent, one at a
     * time, and never rejecting.
     *
     * For work a game does while it's on screen — the next tier, the end-game
     * set — where nothing is waiting on the result. Everything queued here
     * yields to `primeClip`, so a question's read-out lines are never stuck
     * behind a tier's worth of beds.
     *
     * @param {Array<string|Object>} clips
     * @param {{timeoutMs?: number}} [opts]
     * @returns {Promise<{loaded: number, failed: number}>}
     */
    primeInBackground(clips, opts = {}) {
        const list = (Array.isArray(clips) ? clips : []).filter(Boolean);
        if (!list.length) return Promise.resolve({ loaded: 0, failed: 0 });

        const settled = list.map(clip =>
            this.primeClip(clip, { ...opts, priority: "idle" }).then(() => true, () => false)
        );
        return Promise.all(settled).then(results => ({
            loaded: results.filter(Boolean).length,
            failed: results.filter(r => !r).length,
        }));
    }

    /* ---------------- Cache Eviction ---------------- */

    /**
     * Unload a specific clip from the cache to free memory.
     * Used by voHelper to evict a clip whose fetch hung past the safety
     * timeout, releasing the per-origin connection slot.
     * @param {string|Object} clip - The clip definition to unload
     */
    unloadClip(clip) {
        const def = normalizeDefinition(clip);
        if (!def) return;
        const key = cacheKey(def);
        if (!key) return;
        const howl = this.cache.get(key);
        if (!howl) return;
        // Don't unload if still playing
        if (howl.playing()) return;
        audioDiag.count("evicted");
        try { howl.unload(); } catch {}
        this.cache.delete(key);
    }

    /**
     * Evict a set of clips in one go, skipping any that are still playing.
     *
     * The counterpart to a tier-scoped preload: once the ladder has moved past
     * a tier, its bed / down-into / lock-in / stings are dead weight and their
     * decoded buffers are the largest thing in memory. Handing back the tier we
     * just left is what keeps the resident set flat across a whole game instead
     * of climbing to the full ladder's worth.
     *
     * @param {Array<string|Object>} clips
     * @returns {number} how many were actually released
     */
    unloadClips(clips) {
        if (!Array.isArray(clips)) return 0;
        const before = this.cache.size;
        clips.forEach(c => { try { this.unloadClip(c); } catch { } });
        return before - this.cache.size;
    }

    /* =======================================================
     *                 Generic playback helper
     * ======================================================= */

    /**
     * Internal unified play helper.
     * @param {"music"|"sfx"|"vo"} kind
     * @param {object|string|Array} definitionOrUrl
     * @param {object} options
     * @param {object} behavior - per-kind behavior
     *   {
     *     list: Array, cap: number,
     *     computeVolume: (def, opts) => number,
     *     isMuted: () => boolean,
     *     onBeforePlay?: (howl, id, def, opts) => void,   // e.g., set rate
     *     loop: boolean,               // default loop behavior for this kind
     *   }
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    _play(kind, definitionOrUrl, options, behavior) {
        this.init();

        // Resolve array references (if applicable)
        const pureRandom = options?.pureRandom ?? false;
        const resolvedRef = this._resolveArrayReference(definitionOrUrl, pureRandom);

        const def = normalizeDefinition(resolvedRef, { loop: behavior.loop });
        if (!def) {
            console.error(`[AudioManager] Invalid ${kind} play call:`, { def, input: definitionOrUrl });
            return createNoopPlayPromise();
        }

        // `let`, not `const`: a clip that fails to decode is rebuilt as a
        // streaming clip and this is repointed at the replacement. `start()`
        // closes over the variable, so it picks up whichever Howl is current.
        let howl = this._getOrCreateHowl(def, { volume: 0 });
        if (!howl) {
            console.error(`[AudioManager] Failed to create Howl for ${kind}:`, def);
            return createNoopPlayPromise();
        }

        // Extract fade options (default to 0 - no fade)
        const fadeInMs = options?.fadeInMs ?? 0;
        const fadeOutMs = options?.fadeOutMs ?? 0;

        // ---- Timed cues ----
        // Fire callbacks at offsets INSIDE this clip (e.g. "16s in, fade up the
        // next bed"). Scheduled off actual playback start, cleared on every
        // terminal path (stop / end / abort). `at` is ms-from-start, or
        // { fromEnd: ms } to anchor to the clip's tail (needs a known duration).
        const cueTimeoutIds = [];
        const clearCues = () => { for (const t of cueTimeoutIds) clearTimeout(t); cueTimeoutIds.length = 0; };
        const resolveCueAtMs = (at, durationMs) => {
            if (typeof at === "number") return Math.max(0, at);
            if (at && typeof at === "object" && typeof at.fromEnd === "number") {
                return durationMs > 0 ? Math.max(0, durationMs - at.fromEnd) : null;
            }
            return null;
        };

        // ---- Build handle (usable before real start) ----
        let entry = null;
        let pendingStopFade = null;
        let autoFadeTimeoutId = null; // Track the auto-fade timeout
        let startedResolve; let endedResolve;
        const whenStarted = new Promise(res => { startedResolve = res; });
        const whenEnded = new Promise(res => { endedResolve = res; });

        /** @type {PlayHandle} */
        const handle = {
            get id() { return entry?.id ?? null; },
            stop: (customFade) => {
                const useFade = customFade !== undefined ? customFade : fadeOutMs;
                // Clear any pending auto-fade timeout + scheduled cues
                if (autoFadeTimeoutId) {
                    clearTimeout(autoFadeTimeoutId);
                    autoFadeTimeoutId = null;
                }
                clearCues();
                // Notify fade-out start for this particular play call
                try { options?.onFadeOutStart?.(); } catch { }
                if (entry) {
                    this._stopEntry(entry, useFade);
                    this._removeById(behavior.list, entry.id);
                    endedResolve?.();
                } else {
                    // If we haven't started yet, mark to stop on (would-be) start
                    pendingStopFade = useFade;
                }
            },
            fadeTo: (vol, ms = 300) => {
                if (entry) {
                    const newTarget = clamp01(vol);
                    entry.targetVolume = newTarget;
                    const actualVol = behavior.isMuted() ? 0 : newTarget;
                    this._fadeEntry(entry, actualVol, ms);
                }
            },
            // Schedule a one-shot cue at `at` (ms-from-start, or { fromEnd: ms }),
            // resolved against how far this clip has already played. No-op until
            // the clip has started. Cleared with everything else on stop/end.
            addCue: (at, run) => {
                if (typeof run !== "function" || !entry) return;
                const durationMs = (entry.howl.duration(entry.id) || entry.howl.duration() || 0) * 1000;
                const atMs = resolveCueAtMs(at, durationMs);
                if (atMs == null) return;
                const now = performance.now?.() ?? Date.now();
                const elapsedMs = Math.max(0, now - entry.startedAt);
                const t = setTimeout(() => {
                    const i = cueTimeoutIds.indexOf(t); if (i >= 0) cueTimeoutIds.splice(i, 1);
                    try { run(handle); } catch (e) { console.warn("[AudioManager] cue run failed:", e); }
                }, Math.max(0, atMs - elapsedMs));
                cueTimeoutIds.push(t);
            },
            clearCues,
            playing: () => !!(entry && entry.howl.playing(entry.id)),
            whenStarted,
            whenEnded,
        };

        const signal = options?.signal;
        const loop = options?.loop ?? def.loop ?? behavior.loop;
        const targetVol = clamp01(options?.volume ?? def.volume ?? behavior.computeVolume(def, options));
        const timeoutMs = options?.timeoutMs;
        const onFadeInEnd = options?.onFadeInEnd;

        // If already aborted at call time, don't even register load listeners.
        if (signal?.aborted) {
            try { options?.onFadeOutStart?.(); } catch { }
            startedResolve?.(null);
            endedResolve?.();
            const p = Promise.resolve();
            /** @type {any} */ (p).handle = handle;
            return p;
        }

        const playPromise = new Promise(resolve => {
            let settled = false;
            let timeoutId = null;
            let silentEndTimeoutId = null;
            let finished = false;
            let loadListenerAttached = false;

            const finalize = () => {
                if (settled) return;
                settled = true;
                if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
                if (silentEndTimeoutId) { clearTimeout(silentEndTimeoutId); silentEndTimeoutId = null; }
                if (autoFadeTimeoutId) { clearTimeout(autoFadeTimeoutId); autoFadeTimeoutId = null; }
                clearCues();
                resolve();
            };

            // Guarded: a clip that starts late can still reach its real `end`
            // after the silent-end timer below has already finished it.
            const finish = (id) => {
                if (finished) return;
                finished = true;
                this._removeById(behavior.list, id);
                try { options?.onEnd?.(); } catch { }
                endedResolve?.();
                finalize();
            };

            const start = () => {
                // Guard: if aborted before we got to start, do not play.
                if (signal?.aborted || pendingStopFade === -1) {
                    try { options?.onFadeOutStart?.(); } catch { }
                    // indicate "cancel before start"
                    startedResolve?.(null);
                    endedResolve?.();
                    finalize();
                    return;
                }

                const id = howl.play();
                audioDiag.count("played");
                howl.once("playerror", (_id, err) => {
                    audioDiag.count("playError");
                    audioDiag.record(LEVEL.ERROR, `${kind}-playerror`, def.src, String(err ?? ""));
                });
                try { howl.loop(loop, id); } catch { }
                try { howl.volume(0, id); } catch { }

                try { behavior.onBeforePlay?.(howl, id, def, options); } catch { }

                // Determine actual playback volume based on mute state. A ducking
                // channel (SFX) started mid-duck comes in at the ducked level and
                // is recorded so the unduck brings it back to full.
                const startDucked = !!behavior.duckedVolumes && this._isDucked;
                const volTo = behavior.isMuted() ? 0
                    : startDucked ? targetVol * behavior.duckLevel() : targetVol;
                if (startDucked) behavior.duckedVolumes.set(id, targetVol);

                // Only fade if not muted, otherwise just set volume to 0
                if (behavior.isMuted()) {
                    try { howl.volume(0, id); } catch { }
                } else {
                    try { howl.fade(0, volTo, fadeInMs, id); }
                    catch { try { howl.volume(volTo, id); } catch { } }
                }

                if (typeof onFadeInEnd === "function") {
                    setTimeout(() => { try { onFadeInEnd(); } catch { } }, fadeInMs + 10);
                }

                entry = { howl, id, startedAt: performance.now?.() ?? Date.now(), targetVolume: targetVol };

                // Concurrency (drop oldest) - use fadeOutMs for enforcement
                this._enforceLimit(behavior.list, behavior.cap, fadeOutMs);

                behavior.list.push(entry);
                startedResolve?.(entry);

                // Schedule any timed cues now that playback has actually begun.
                if (Array.isArray(options?.cues) && options.cues.length) {
                    const durationMs = (howl.duration(id) || howl.duration() || 0) * 1000;
                    for (const cue of options.cues) {
                        if (!cue || typeof cue.run !== "function") continue;
                        const atMs = resolveCueAtMs(cue.at, durationMs);
                        if (atMs == null) {
                            console.warn("[AudioManager] cue skipped (needs known duration for fromEnd):", def.src);
                            continue;
                        }
                        const t = setTimeout(() => {
                            const i = cueTimeoutIds.indexOf(t); if (i >= 0) cueTimeoutIds.splice(i, 1);
                            try { cue.run(handle); } catch (e) { console.warn("[AudioManager] cue run failed:", e); }
                        }, atMs);
                        cueTimeoutIds.push(t);
                    }
                }

                // Auto fade-out for non-looping audio based on clip duration
                if (!loop && fadeOutMs > 0) {
                    const duration = howl.duration(id); // in seconds
                    if (duration > 0) {
                        const durationMs = duration * 1000;
                        const fadeStartMs = Math.max(0, durationMs - fadeOutMs);

                        autoFadeTimeoutId = setTimeout(() => {
                            autoFadeTimeoutId = null;
                            // Only fade if still playing and not muted
                            if (howl.playing(id)) {
                                if (entry) entry.fadingOut = true; // duck/unduck must not fight this fade
                                try {
                                    const currentVol = howl.volume(id);
                                    howl.fade(currentVol, 0, fadeOutMs, id);
                                    // Trigger fade-out callback if provided
                                    options?.onFadeOutStart?.();
                                } catch (e) {
                                    console.warn(`[AudioManager] Auto fade-out failed for ${kind}:`, e);
                                }
                            }
                        }, fadeStartMs);
                    }
                }

                // If stop was requested before start, stop immediately without letting it "play"
                if (pendingStopFade != null) {
                    try { options?.onFadeOutStart?.(); } catch { }
                    this._stopEntry(entry, pendingStopFade);
                    this._removeById(behavior.list, id);
                    endedResolve?.();
                    finalize();
                    return;
                }

                // Timeout logic (optionally stop on timeout)
                if (timeoutMs && timeoutMs > 0) {
                    timeoutId = setTimeout(() => {
                        if (options?.stopOnTimeout) {
                            try { options?.onFadeOutStart?.(); } catch { }
                            handle.stop(); // uses fadeOutMs from options
                            // stop listener below will finalize
                        } else {
                            finalize(); // just resolve; playback continues
                        }
                    }, timeoutMs);
                }

                const onFinish = () => finish(id);
                // Only listen to 'end' for non-looped audio
                if (!loop) {
                    howl.once("end", onFinish, id);
                }
                howl.once("stop", onFinish, id);

                // Silent-page safety net (see PLAY_END_GRACE_MS). The length is
                // known once loaded, whether or not the clip can play. Unknown
                // length → no net, and the caller's own timeout still applies.
                if (!loop) {
                    const rate = howl.rate(id) || 1;
                    const durationMs = ((howl.duration(id) || howl.duration() || 0) * 1000) / rate;
                    if (durationMs > 0) {
                        silentEndTimeoutId = setTimeout(() => {
                            silentEndTimeoutId = null;
                            if (settled) return;
                            // Never started → stop the held play, or every clip
                            // skipped this way would fire at once when the page
                            // is finally clicked. A late start just plays out.
                            if (this._silenceIfHeld(howl, id)) {
                                audioDiag.record(LEVEL.WARN, `${kind}-silent`, def.src, `never started, waited ${Math.round(durationMs)}ms`);
                                try { howl.stop(id); } catch { }
                            }
                            finish(id);
                        }, durationMs + PLAY_END_GRACE_MS);
                    }
                }
            };

            const onLoad = start;

            // Wait for `howl` to load, then start. Split out so the streaming
            // retry below can re-arm it against a different Howl.
            const awaitLoadThenStart = () => {
                const st = howl.state();
                if (st === "unloaded") { try { howl.load(); } catch { } }
                if (st === "loaded") {
                    start();
                    return;
                }
                loadListenerAttached = true;
                howl.once("load", onLoad);
                howl.once("loaderror", onLoadError);
            };

            // A clip that fails to load never starts, so without this the play
            // promise never settles and every `await audio.playX(...)` on that
            // clip hangs for the life of the page — an invisible stall that
            // looks like the game going slow rather than like an audio fault.
            //
            // First, though, give it the one recovery that's actually likely to
            // work: if the decode failed because this device has no room for
            // the buffer, the same file streams fine. Swap in a streaming Howl
            // and wait on that instead. `_fallbackToStreaming` returns null if
            // the clip was ALREADY streaming, which is what bounds this to a
            // single retry — a stream that fails is a real failure.
            //
            // If there's no retry to make, resolve, so a caller sequencing on
            // audio carries on in silence rather than hanging. The overlay
            // still shows it (recorded in _getOrCreateHowl).
            const onLoadError = () => {
                loadListenerAttached = false;
                try { howl.off("load", onLoad); } catch { }

                const streamed = this._fallbackToStreaming(def);
                if (streamed && !signal?.aborted && pendingStopFade !== -1) {
                    howl = streamed;          // `start()` reads this variable
                    awaitLoadThenStart();
                    return;
                }

                startedResolve?.(null);
                endedResolve?.();
                finalize();
            };

            awaitLoadThenStart();

            // Abort handling: before-start cancellation should *not* play anything.
            if (signal) {
                signal.addEventListener("abort", () => {
                    // If we haven't started yet, prevent start and resolve immediately.
                    if (!entry) {
                        try { options?.onFadeOutStart?.(); } catch { }
                        // mark: cancel-before-start (used by start guard above)
                        pendingStopFade = -1;
                        if (loadListenerAttached) {
                            try { howl.off("load", onLoad); } catch { }
                            try { howl.off("loaderror", onLoadError); } catch { }
                            loadListenerAttached = false;
                        }
                        startedResolve?.(null);
                        endedResolve?.();
                        finalize();
                    } else {
                        // If already started, stop normally.
                        handle.stop(); // uses fadeOutMs from options
                    }
                }, { once: true });
            }
        });

        /** @type {any} */ (playPromise).handle = handle;
        return playPromise;
    }


    /* ---------------- MUSIC ---------------- */

    /**
     * Effective playback volume for a music target, accounting for mute + duck.
     * Mirrors the logic the generic _play/duck paths apply so crossfade-loop
     * instances start at the right level even when spun up mid-duck.
     * @param {number} target
     * @returns {number}
     */
    _effectiveMusicVolume(target) {
        if (this.musicMuted) return 0;
        if (this._isDucked) return target * this.duckLevel;
        return target;
    }

    /**
     * Loop a clip by CROSSFADING its own tail into a fresh head, instead of
     * Howler's native (hard-cut) loop. A second instance of the same Howl is
     * started `crossfadeMs` before the current one ends; the outgoing instance
     * fades to 0 while the incoming fades up, so the loop seam is smoothed over
     * rather than snapping. Each live instance is registered in `activeMusic`,
     * so mute / duck / volume / stopAllMusic all reach it like any other music.
     *
     * Returns the same `{ handle }` shape as playMusic. The promise resolves
     * only when the loop is stopped (via handle.stop / abort / stopAllMusic).
     *
     * @param {object} def - normalized definition ({ src, volume?, loop? })
     * @param {object} options - playMusic options (crossfadeMs, fadeInMs, volume, signal, ...)
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    _playMusicCrossfadeLoop(def, options = {}) {
        const howl = this._getOrCreateHowl(def, { volume: 0 });
        if (!howl) {
            console.error("[AudioManager] Failed to create Howl for crossfade loop:", def);
            return createNoopPlayPromise();
        }

        const targetVol = clamp01(options?.volume ?? def.volume ?? this.musicDefaultVolume);
        const fadeInMs = options?.fadeInMs ?? 0;
        const fadeOutMs = options?.fadeOutMs ?? 0;
        const requestedXfade = options?.crossfadeMs ?? 0;
        const signal = options?.signal;

        let stopped = false;
        let firstStartResolved = false;
        let scheduleTimeoutId = null;
        /** @type {{howl: Howl, id: number, startedAt: number, targetVolume: number}|null} */
        let currentEntry = null;

        let startedResolve; let endedResolve;
        const whenStarted = new Promise(res => { startedResolve = res; });
        const whenEnded = new Promise(res => { endedResolve = res; });

        const controller = {
            cancel: () => stop(0),
        };

        const clearSchedule = () => {
            if (scheduleTimeoutId) { clearTimeout(scheduleTimeoutId); scheduleTimeoutId = null; }
        };

        // Start one instance, fade it up, and schedule the next crossfade.
        const spinUp = (fadeMs) => {
            if (stopped) return;
            const id = howl.play();
            howl.once("playerror", (_id, err) => {
                audioDiag.count("playError");
                audioDiag.record(LEVEL.ERROR, "music-playerror", def.src, String(err ?? ""));
            });
            try { howl.loop(false, id); } catch { }   // we drive the loop ourselves
            try { howl.volume(0, id); } catch { }

            const entry = { howl, id, startedAt: performance.now?.() ?? Date.now(), targetVolume: targetVol };
            const volTo = this._effectiveMusicVolume(targetVol);
            // If we spin up mid-duck, mirror what _duckMusic tracks so unduck restores correctly.
            if (this._isDucked && !this.musicMuted) this._musicUnduckedVolumes.set(id, targetVol);
            try { howl.fade(0, volTo, fadeMs, id); }
            catch { try { howl.volume(volTo, id); } catch { } }

            this.activeMusic.push(entry);
            currentEntry = entry;

            if (!firstStartResolved) {
                firstStartResolved = true;
                startedResolve?.(entry);
            }

            // Schedule the crossfade to the next instance near this clip's end,
            // timed from when it ACTUALLY starts. While audio is locked Howler
            // holds the play, and a wall-clock schedule kept spinning up more
            // held copies, which all started together on the first click.
            howl.once("play", () => scheduleNext(entry, id), id);
        };

        const scheduleNext = (entry, id) => {
            if (stopped) return;
            const durationSec = howl.duration(id) || howl.duration() || 0;
            if (durationSec > 0) {
                const durationMs = durationSec * 1000;
                // Overlap can't exceed the clip; keep at least half the clip un-overlapped.
                const xfade = Math.min(requestedXfade, Math.floor(durationMs / 2));
                const nextAtMs = Math.max(0, durationMs - xfade);
                clearSchedule();
                scheduleTimeoutId = setTimeout(() => {
                    scheduleTimeoutId = null;
                    if (stopped) return;
                    const outgoing = entry;
                    // Fade the outgoing tail down and stop it once silent.
                    try { outgoing.howl.fade(outgoing.howl.volume(outgoing.id), 0, xfade, outgoing.id); } catch { }
                    setTimeout(() => {
                        try { outgoing.howl.stop(outgoing.id); } catch { }
                        this._removeById(this.activeMusic, outgoing.id);
                        this._musicUnduckedVolumes.delete(outgoing.id);
                    }, xfade + 10);
                    // Bring the next instance in over the same window.
                    spinUp(xfade);
                }, nextAtMs);
            }
            // If duration is somehow unavailable, we simply don't loop — the clip
            // plays once. (Shouldn't happen: beds are preloaded before playback.)
        };

        function stop(customFade) {
            const useFade = customFade !== undefined ? customFade : fadeOutMs;
            if (stopped) return;
            stopped = true;
            clearSchedule();
            try { options?.onFadeOutStart?.(); } catch { }
            // Fade out every live instance of this loop and drop them from activeMusic.
            const mgr = self;
            mgr.activeMusic.slice().forEach(e => {
                if (e.howl !== howl) return;
                mgr._stopEntry(e, useFade);
                mgr._removeById(mgr.activeMusic, e.id);
                mgr._musicUnduckedVolumes.delete(e.id);
            });
            mgr._musicLoops.delete(controller);
            currentEntry = null;
            endedResolve?.();
        }
        const self = this;

        /** @type {PlayHandle} */
        const handle = {
            get id() { return currentEntry?.id ?? null; },
            stop: (customFade) => stop(customFade),
            fadeTo: (vol, ms = 300) => {
                const newTarget = clamp01(vol);
                // Retarget every live instance of this loop.
                self.activeMusic.forEach(e => {
                    if (e.howl !== howl) return;
                    e.targetVolume = newTarget;
                    const actualVol = self._effectiveMusicVolume(newTarget);
                    self._fadeEntry(e, actualVol, ms);
                });
            },
            playing: () => self.activeMusic.some(e => e.howl === howl && e.howl.playing(e.id)),
            whenStarted,
            whenEnded,
        };

        this._musicLoops.add(controller);

        if (signal) {
            if (signal.aborted) { stop(0); }
            else signal.addEventListener("abort", () => stop(), { once: true });
        }

        const begin = () => spinUp(fadeInMs);
        const st = howl.state();
        if (st === "unloaded") { try { howl.load(); } catch { } }
        if (st === "loaded") begin();
        else howl.once("load", () => { if (!stopped) begin(); });

        /** @type {any} */ (whenEnded).handle = handle;
        return /** @type {any} */ (whenEnded);
    }

    /**
     * Play background music (looped by default).
     * Returns a Promise that resolves when playback ends/stops (or timeout). Use the returned `.handle`
     * for mid-play control, and optional `onFadeInEnd` / `onFadeOutStart` callbacks for UI timing.
     *
     * Pass `crossfadeMs` (with loop) to smooth the loop seam: instead of Howler's
     * hard-cut native loop, the clip's tail crossfades into a fresh head over that
     * window. Use it for short beds that don't loop seamlessly.
     *
     * Pass `cues` to trigger callbacks at offsets INSIDE the clip — e.g. play a
     * long opening sting and, 16s in (as it settles into its drone tail), fade up
     * the looping bed underneath it so the bed is already going before the sting
     * ends. Cues are cleared automatically on stop / end / abort. NOTE: cues run
     * on the generic play path, so they apply to native-loop and one-shot music;
     * they're ignored on the `crossfadeMs` loop path (put cues on the sting that
     * triggers the bed, not on the crossfaded bed itself).
     * @param {string|Object|Array} definitionOrUrl
     * @param {{
     *   loop?:boolean,
     *   crossfadeMs?:number,
     *   fadeInMs?:number,
     *   fadeOutMs?:number,
     *   volume?:number,
     *   timeoutMs?:number,
     *   stopOnTimeout?:boolean,
     *   signal?:AbortSignal,
     *   pureRandom?:boolean,
     *   cues?:Array<{at:number|{fromEnd:number}, run:(handle:PlayHandle)=>void}>,
     *   onEnd?:Function,
     *   onFadeInEnd?:Function,
     *   onFadeOutStart?:Function
     * }} [options]
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    playMusic(definitionOrUrl, options = {}) {
        // Crossfade-loop path: only when we're actually looping with an overlap.
        const wantsLoop = options?.loop ?? true;
        if (wantsLoop && (options?.crossfadeMs ?? 0) > 0) {
            this.init();
            const resolvedRef = this._resolveArrayReference(definitionOrUrl, options?.pureRandom ?? false);
            const def = normalizeDefinition(resolvedRef, { loop: true });
            if (!def) {
                console.error("[AudioManager] Invalid music (crossfade) play call:", definitionOrUrl);
                return createNoopPlayPromise();
            }
            return this._playMusicCrossfadeLoop(def, options);
        }
        return this._play("music", definitionOrUrl, options, {
            list: this.activeMusic,
            cap: MAX_CONCURRENT_MUSIC,
            computeVolume: (_def, _opts) => this.musicDefaultVolume,
            isMuted: () => this.musicMuted,
            onBeforePlay: (_howl, _id) => { /* no-op for music */ },
            loop: true,
        });
    }

    /* ---------------- Handing off between clips ---------------- */

    /**
     * How much of a given SFX is still to play, in ms. 0 if it isn't playing.
     *
     * For handing off between a clip started somewhere else and one that must
     * not talk over it — the £1,000,000 sting fires on the reveal and is still
     * ringing when the celebration screen opens, which is long after the code
     * that started it has stopped caring. The play promise only exists at the
     * call site, so a later screen needs to ask the manager instead.
     *
     * Takes the same definition or url as playSfx, and reads the live position
     * off the Howl rather than working from the start time, so a clip that was
     * seeked, paused by a tab switch, or stopped early answers honestly.
     *
     * @param {string|Object|Array} definitionOrUrl
     * @returns {number} Milliseconds left on the longest matching instance.
     */
    sfxRemainingMs(definitionOrUrl) {
        // Read-only, so an array reference is checked in FULL rather than
        // resolved: _resolveArrayReference would advance that slot's variation
        // cycle, and a query has no business changing what plays next.
        const refs = Array.isArray(definitionOrUrl) ? definitionOrUrl : [definitionOrUrl];
        const howls = [];
        for (const ref of refs) {
            const def = normalizeDefinition(ref);
            if (!def) continue;
            const howl = this.cache.get(cacheKey(def));
            if (howl) howls.push(howl);   // absent = never played, so never loaded
        }
        if (!howls.length) return 0;

        let remaining = 0;
        for (const entry of this.activeSfx) {
            if (!howls.includes(entry.howl)) continue;
            const howl = entry.howl;
            try {
                // The playing() check is load-bearing, not just an optimisation.
                // Howler's one-argument seek() treats its argument as a sound id
                // only if that id is still live; for a finished one it falls
                // through to "this must be a new seek position" and SETS the
                // playhead to it. Asking about a dead entry would therefore
                // scrub a different instance of the same clip to some absurd
                // offset. playing() is false for an unknown id, so this skips.
                if (!howl.playing(entry.id)) continue;
                const durationMs = (howl.duration(entry.id) || howl.duration() || 0) * 1000;
                const seek = howl.seek(entry.id);
                const positionMs = (typeof seek === "number" ? seek : 0) * 1000;
                remaining = Math.max(remaining, durationMs - positionMs);
            } catch { /* entry went away mid-check */ }
        }
        return Math.max(0, remaining);
    }

    /**
     * Stop the most recent music entry.
     * @param {number} [fadeMs=0] - Fade duration in ms
     */
    stopMusic(fadeMs = 0) {
        const last = this.activeMusic.pop();
        if (!last) return;
        this._stopEntry(last, fadeMs);
    }

    /**
     * Stop a specific music entry by id.
     * @param {number} id
     * @param {number} [fadeMs=0] - Fade duration in ms
     */
    stopMusicById(id, fadeMs = 0) {
        const idx = this.activeMusic.findIndex(e => e.id === id);
        if (idx === -1) return;
        const [entry] = this.activeMusic.splice(idx, 1);
        this._stopEntry(entry, fadeMs);
    }

    /** Stop all music immediately. */
    stopAllMusic() {
        // Cancel crossfade-loop schedulers first so they can't spin up a new
        // instance after we've cleared the list.
        this._musicLoops.forEach(c => { try { c.cancel(); } catch { } });
        this._musicLoops.clear();
        this.activeMusic.forEach(e => { try { e.howl.stop(e.id); } catch { } });
        this.activeMusic.length = 0;
    }

    /** Set default music volume (0..1). Applies to current if not muted. */
    setMusicVolume(value) {
        this.musicDefaultVolume = clamp01(value);
        const last = this.activeMusic[this.activeMusic.length - 1];
        if (last?.howl && !this.musicMuted) {
            last.targetVolume = this.musicDefaultVolume;
            last.howl.volume(this.musicDefaultVolume, last.id);
        }
    }

    /**
     * Mute/unmute music (sets volume to 0 but keeps playing). Unmuting while
     * ducked comes back at the DUCKED level, not full — this also runs from
     * synced room state on every refresh, so it mustn't blast music under a VO.
     */
    muteMusic(mute = true) {
        if (this.musicMuted === !!mute) return;
        this.musicMuted = !!mute;
        this.activeMusic.forEach(e => {
            try {
                const unducked = this._musicUnduckedVolumes.get(e.id) ?? e.targetVolume;
                e.howl.volume(this._effectiveMusicVolume(unducked), e.id);
            } catch { }
        });
    }

    /* ---------------- SFX ---------------- */

    /**
     * Play a one-shot sound effect.
     * Returns a Promise that resolves when playback ends/stops (or timeout). Optional `onFadeInEnd` /
     * `onFadeOutStart` can be used (though SFX typically use tiny fades).
     * @param {string|Object|Array} definitionOrUrl
     * @param {{
     *   volume?:number,
     *   rate?:number,
     *   fadeInMs?:number,
     *   fadeOutMs?:number,
     *   timeoutMs?:number,
     *   stopOnTimeout?:boolean,
     *   signal?:AbortSignal,
     *   pureRandom?:boolean,
     *   onEnd?:Function,
     *   onFadeInEnd?:Function,
     *   onFadeOutStart?:Function
     * }} [options]
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    playSfx(definitionOrUrl, options = {}) {
        return this._play("sfx", definitionOrUrl, options, {
            list: this.activeSfx,
            cap: MAX_CONCURRENT_SFX,
            computeVolume: (def, opts) => {
                const base = def.volume != null ? clamp01(def.volume) : this.sfxDefaultVolume;
                const mult = opts?.volume != null ? clamp01(opts.volume) : 1;
                return base * mult;
            },
            isMuted: () => this.sfxMuted,
            duckedVolumes: this._sfxUnduckedVolumes,
            duckLevel: () => this.sfxDuckLevel,
            onBeforePlay: (howl, id, _def, opts) => {
                const rate = opts?.rate ?? 1.0;
                try { howl.rate(rate, id); } catch { }
            },
            loop: false,
        });
    }

    playSfxWithAwaitedLoad(definitionOrUrl, options = {}) {
        return new Promise((resolve) => {
            let playEventFired = false;

            this._play("sfx", definitionOrUrl, options, {
                list: this.activeSfx,
                cap: MAX_CONCURRENT_SFX,
                fadeCapMs: 60,
                computeVolume: (def, opts) => {
                    const base = def.volume != null ? clamp01(def.volume) : this.sfxDefaultVolume;
                    const mult = opts?.volume != null ? clamp01(opts.volume) : 1;
                    return base * mult;
                },
                isMuted: () => this.sfxMuted,
                duckedVolumes: this._sfxUnduckedVolumes,
                duckLevel: () => this.sfxDuckLevel,
                onBeforePlay: (howl, id, _def, opts) => {
                    const rate = opts?.rate ?? 1.0;
                    try { howl.rate(rate, id); } catch { }

                    // Listen for when playback actually starts
                    howl.once('play', () => {
                        playEventFired = true;
                        resolve();
                    }, id);
                },
                loop: false,
                fadeInMs: 20,
            });

            // Fallback: if play event doesn't fire within reasonable time, resolve anyway
            setTimeout(() => {
                if (!playEventFired) {
                    resolve();
                }
            }, 5000)
        });
    }

    /** Stop all SFX immediately. */
    stopAllSfx() {
        this.activeSfx.forEach(e => { try { e.howl.stop(e.id); } catch { } });
        this.activeSfx.length = 0;
    }

    /** Set default SFX volume (0..1). */
    setSfxVolume(value) {
        this.sfxDefaultVolume = clamp01(value);
        this.activeSfx.forEach(e => {
            e.targetVolume = this.sfxDefaultVolume;
            if (!this.sfxMuted) {
                try { e.howl.volume(this.sfxDefaultVolume, e.id); } catch { }
            }
        });
    }

    /** Mute/unmute all SFX (sets volume to 0 but keeps playing). */
    muteSfx(mute = true) {
        if (this.sfxMuted === !!mute) return;
        this.sfxMuted = !!mute;
        this.activeSfx.forEach(e => {
            try {
                // Unmuting mid-duck comes back at the ducked level (same as music).
                const unducked = this._sfxUnduckedVolumes.get(e.id) ?? e.targetVolume;
                const ducked = this._isDucked && !e.fadingOut;
                const newVol = this.sfxMuted ? 0 : ducked ? unducked * this.sfxDuckLevel : unducked;
                e.howl.volume(newVol, e.id);
            } catch { }
        });
    }

    /* ---------------- VO ---------------- */

    /**
     * Play a voice-over line.
     * Returns a Promise that resolves when playback ends/stops (or timeout). Use `onFadeInEnd` /
     * `onFadeOutStart` to hook UI transitions around VO.
     * @param {string|Object|Array} definitionOrUrl
     * @param {{
     *   volume?:number,
     *   fadeInMs?:number,
     *   fadeOutMs?:number,
     *   timeoutMs?:number,
     *   stopOnTimeout?:boolean,
     *   signal?:AbortSignal,
     *   pureRandom?:boolean,
     *   onEnd?:Function,
     *   onFadeInEnd?:Function,
     *   onFadeOutStart?:Function
     * }} [options]
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    playVo(definitionOrUrl, options = {}) {
        // Duck music + SFX and notify listeners (e.g. VideoPlayer) before playback
        // so the fade-down is already in flight by the time the VO starts.
        this._duck();

        const result = this._play("vo", definitionOrUrl, options, {
            list: this.activeVo,
            cap: MAX_CONCURRENT_VO,
            computeVolume: (def, opts) => {
                const base = def.volume != null ? clamp01(def.volume) : this.voDefaultVolume;
                const mult = opts?.volume != null ? clamp01(opts.volume) : 1;
                return base * mult;
            },
            isMuted: () => this.voMuted,
            onBeforePlay: (_howl, _id) => { /* no-op for VO */ },
            loop: false,
        });

        // Unduck once the last active VO has ended. Concurrent VOs share the
        // duck (only the final one releases), and an open holdDuck keeps it.
        result.then(() => this._maybeUnduck());

        return result;
    }

    /** Stop all currently playing VO immediately. */
    stopAllVo() {
        this.activeVo.forEach(e => { try { e.howl.stop(e.id); } catch { } });
        this.activeVo.length = 0;
    }

    /**
     * Stop the most recent VO (useful on state changes).
     * @param {number} [fadeMs=0] - Fade duration in ms
     */
    stopVo(fadeMs = 0) {
        const last = this.activeVo.pop();
        if (!last) return;
        this._stopEntry(last, fadeMs);
    }

    /** Set default VO volume (0..1). */
    setVoVolume(value) {
        this.voDefaultVolume = clamp01(value);
        this.activeVo.forEach(e => {
            e.targetVolume = this.voDefaultVolume;
            if (!this.voMuted) {
                try { e.howl.volume(this.voDefaultVolume, e.id); } catch { }
            }
        });
    }

    /** Mute/unmute all VO (sets volume to 0 but keeps playing). */
    muteVo(mute = true) {
        if (this.voMuted === !!mute) return;
        this.voMuted = !!mute;
        this.activeVo.forEach(e => {
            try {
                const newVol = this.voMuted ? 0 : e.targetVolume;
                e.howl.volume(newVol, e.id);
            } catch { }
        });
    }

    /* ---------------- Master + Sequence ---------------- */

    /** Stop all audio (music, SFX, and VO) immediately. */
    stopAllAudio() {
        this.stopAllMusic();
        this.stopAllSfx();
        this.stopAllVo();
    }

    /** Mute/unmute all audio globally. */
    setMasterMute(mute = true) {
        Howler.mute(!!mute);
    }

    /**
     * Run a sequence of audio actions in order.
     * Accepts music(), sfx(), vo(), wait(ms), or numbers as delays.
     * @param {Array} steps
     * @returns {Promise<void>}
     */
    async sequence(steps = []) {
        for (const step of steps) {
            if (step == null) continue;
            if (typeof step === "number") { await delay(step); continue; }
            if (step.type === "wait") { await delay(step.ms || 0); continue; }
            if (step.type === "music") { await this.playMusic(step.ref, step.opts || {}); continue; }
            if (step.type === "sfx") { await this.playSfx(step.ref, step.opts || {}); continue; }
            if (step.type === "vo") { await this.playVo(step.ref, step.opts || {}); continue; }
        }
    }
}

/* ---------- Convenience builders for sequence() ---------- */

/**
 * Create a music step for use in an audio sequence.
 * @param {*} ref
 * @param {*} opts
 * @returns {{type:"music", ref:any, opts?:any}}
 */
export function music(ref, opts) { return { type: "music", ref, opts }; }

/**
 * Create a sound effect step for use in an audio sequence.
 * @param {*} ref
 * @param {*} opts
 * @returns {{type:"sfx", ref:any, opts?:any}}
 */
export function sfx(ref, opts) { return { type: "sfx", ref, opts }; }

/**
 * Create a voice-over step for use in an audio sequence.
 * @param {*} ref
 * @param {*} opts
 * @returns {{type:"vo", ref:any, opts?:any}}
 */
export function vo(ref, opts) { return { type: "vo", ref, opts }; }

/**
 * Create a wait/delay step for use in an audio sequence.
 * @param {number} ms
 * @returns {{type:"wait", ms:number}}
 */
export function wait(ms) { return { type: "wait", ms }; }

/** Singleton AudioManager instance. */
export const audio = new AudioManager();

// Reachable without an import, for a console and for PerfOverlay. Without this
// PerfOverlay falls back to estimating from Howler, which knows nothing about
// streamed clips or the cache budget, so its audio figure would quietly
// disagree with AudioDebugOverlay's.
if (typeof window !== "undefined") window.__audioManager = audio;